import {Component, ChangeDetectorRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Observable, Subject, forkJoin} from "rxjs";
import {CentreService} from "../../../services/centre.service";
import {Centre} from "../../../interfaces/Centres";
import {ChildService} from "../../child/child.service";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexStroke,
  ApexDataLabels,
  ApexFill,
  ApexLegend, ChartComponent
} from 'ng-apexcharts';
import {EducationService} from "../../../modules/education/education.service";
import {MatTableDataSource} from "@angular/material/table";
import {filter, take} from "rxjs/operators";
import {Employee} from "../../../interfaces/Employee";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {LoadingService} from "../../../services/loading.service";
import {EmployeeService} from "../../../services/employee.service";
import {SubSink} from "../../../shared/sub-sink";
import {NotificationModel, NotificationService} from "../../../services/notification.service";
import {MatSelectChange} from "@angular/material/select";
import {UserData} from "../../../auth.service";
import {EducationObservationService} from "../../../modules/education/components/educationObservation/education-observation.service";

export interface ChartOptionsArea {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  fill: ApexFill;
  legend: ApexLegend;
  colors: string[];
}

export interface ChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  stroke: ApexStroke;
  fill: ApexFill;
  legend: ApexLegend;
  colors: string[];
  grid: any;
  tooltip: any;
  plotOptions: any;
}


export interface Teacher {
  profileImages: string;
  userName: string;
  designation: string;
  centre: string[];
  individualSessionCount: number;
  groupSessionCount: number;
  followUpCount: number;
  sessionObserve: number;
  disabled?: boolean;
  expanded?: boolean;
  isSynthetic?: boolean; // true for users created from session data (not in user list)
}

export interface CentreChildrenRow {
  srNo: number;
  date: string;
  centreName: string;
  centreCode: string;
  below14New: number;
  below14Foll: number;
  above14New: number;
  above14Foll: number;
  total: number;
}

export interface MissingSessionsLogEdu {
  reason: 'Empty Name (Excluded)' | 'Excluded Name (Filtered)' | 'Unmatched (Active Mode)';
  teacherName: string;
  normalizedKey: string;
  individual: number;
  group: number;
  followUp: number;
  centreCode?: string;
  docCount?: number;
}

export interface MissingSessionsReportEdu {
  rawTotals:  { individual: number; group: number; followUp: number; total: number };
  cardTotals: { individual: number; group: number; followUp: number; total: number };
  diff:       { individual: number; group: number; followUp: number; total: number };
  logs: MissingSessionsLogEdu[];
  generatedAt: Date;
}

@Component({
  selector: 'app-mis-education',
  templateUrl: './mis-education.component.html',
  styleUrls: ['./mis-education.component.scss']
})
export class MisEducationComponent implements OnInit, OnDestroy {
  @ViewChild("areaChart") areaChart!: ChartComponent;
  @ViewChild("barChart") barChart!: ChartComponent;

  public chartOptionsArea: ChartOptionsArea;
  public barChartOptions: ChartOptions;
  selectedCentreObjects: any[] = [];

  allUsers: Teacher[] = [];
  teacherColumns: string[] = [
    'no',
    'photo',
    'userName',
    'centre',
    // 'individualSessionCount',
    // 'groupSessionCount',
    // 'followUpCount',
    'totalSessionCount',
    'SessionObservedCount'
  ];

  tableObservedTotal = 0;

  dataSource = new MatTableDataSource<Teacher>([]);
  isSeniorTeacher = true;
  selectedCentres: string[] = [];
  centres$: Observable<Centre[]>;
  allowedCentres$: Observable<Centre[]>;
  mStartDate: Date | null = null; // null = fetch all data from the beginning
  mEndDate: Date | null = new Date();
  destroyed$ = new Subject();

  /**
   * Returns April 1 of the current Indian financial year.
   * FY runs April 1 – March 31.
   * If current month is Jan–Mar, FY started on April 1 of previous year.
   */
  private static getFinancialYearStart(): Date {
    const now = new Date();
    const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear(); // Jan=0,Feb=1,Mar=2
    return new Date(year, 3, 1); // April 1
  }

  teachersType: 'Active' | 'All' = 'Active';

  // ✅ Performance optimization properties
  private subs = new SubSink();
  private teacherMap = new Map<string, Teacher>();
  private searchSubject = new Subject<void>();

  /**
   * ✅ Name alias map: maps variant/alternate names → canonical (primary) name.
   * All keys and values must be lowercase trimmed.
   * When a summary doc uses an alternate name, it will be merged into the canonical teacher.
   */
  private readonly nameAliases: { [alias: string]: string } = {
    'ankita ajay shelar': 'ankita shelar',
    // Add more aliases here as needed, e.g.:
    // 'john a. doe': 'john doe',
  };

  /**
   * Normalize a teacher name: lowercase, trim, and resolve aliases to the canonical name.
   */
  private normalizeName(name: string): string {
    const key = (name || '').toLowerCase().trim();
    return this.nameAliases[key] || key;
  }

  displayedColumns: string[] = ['sr', 'title', 'message', 'date', 'status'];

  educationSessionCounts: any;
  groupSessionCounts: any;
  followUpCounts: any = 0;
  combinedSessionCounts: any;

  educationSessions: any[] = [];
  groupSessions: any[] = [];
  followUpSessions: any[] = [];
  rolePriority: any = {
    'lead teacher': 1,
    'sr teacher': 2,
    'teacher': 3,
  };


  notifications: NotificationModel[] = [];
  userData: UserData = JSON.parse(localStorage.getItem('userData')!) as UserData
  protected showNotifications = false;

  centreChildrenRows: CentreChildrenRow[] = [];
  centreChildrenDataSource = new MatTableDataSource<CentreChildrenRow>([]);
  centreChildrenColumns: string[] = [
    'srNo', 'dateRange', 'centreName', 'totalChildren', 'children4to14', 'childrenAbove14', 'followUpCount'
  ];

  childStartDate: Date | null = null;
  childEndDate: Date | null = null;
  childSelectedCentres: string[] = [];
  childSelectedCentreObjects: any[] = [];

  /** Debug / discrepancy report populated after each search() */
  debugReport: MissingSessionsReportEdu | null = null;
  showDebugButton = false;
  showDebugPanel = false;

  constructor(
    private centerService: CentreService,
    private childService: ChildService,
    private educationService: EducationService,
    private mDatabase: AngularFireDatabase,
    public loadingService: LoadingService,
    private readonly mNotificationService: NotificationService,
    private employeeService: EmployeeService,
    private cdr: ChangeDetectorRef,
    private educationObservationService: EducationObservationService
  ) {
    this.centres$ = this.centerService.getAllCentres();
    this.allowedCentres$ = this.centerService.getAllCentres();
    this.getCurrentUserNotifications(this.userData.email)
    this.chartOptionsArea = {
      series: [
        {name: "Boys", data: [45, 52, 38, 24, 33, 26, 21]},
        {name: "Girls", data: [35, 41, 62, 42, 13, 18, 29]}
      ],
      chart: {
        type: "area",
        height: 350,
        toolbar: {show: false}
      },
      dataLabels: {enabled: false},
      stroke: {curve: "smooth", width: 3},
      xaxis: {categories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]},
      yaxis: {title: {text: "Boys Vs Girls"}},
      colors: ["#fc8380", "#6973c6"],
      fill: {type: "gradient"},
      legend: {position: "top", horizontalAlign: "left"}
    };

    this.barChartOptions = {
      series: [],
      chart: {
        type: 'bar',
        height: 500,
        stacked: true,
        toolbar: {show: true}
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '70%'
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#fff']
      },
      xaxis: {
        categories: []
      },
      yaxis: {
        title: {
          text: 'Centre'
        }
      },
      legend: {
        position: 'top'
      },
      fill: {
        type: 'solid'
      },
      colors: ['#4CAF50', '#2196F3', '#FF9800'],
      grid: {
        borderColor: '#e0e0e0'
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    };

  }

  ngOnInit() {
    this.search(); // ✅ Auto-load all counts for the current financial year on init
  }

  buildCentrePerformanceChart() {

    const centreMap: any = {};

    // 🔥 Aggregate teacher data into centres
    this.allUsers.forEach(teacher => {

      teacher.centre.forEach(c => {

        if (!centreMap[c]) {
          centreMap[c] = {
            individual: 0,
            group: 0,
            followUp: 0
          };
        }

        centreMap[c].individual += teacher.individualSessionCount;
        centreMap[c].group += teacher.groupSessionCount;
        centreMap[c].followUp += teacher.followUpCount;

      });

    });

    // Filter out non-centre entries from chart
    const excludedFromChart = ['all', 'all centre', 'all centres', 'pan india', 'mumbai - chembur & sandu garden'];
    const centreNames = Object.keys(centreMap).filter(
      c => !excludedFromChart.includes(c.toLowerCase())
    );

    const individualData = centreNames.map(c => centreMap[c].individual);
    const groupData = centreNames.map(c => centreMap[c].group);
    const followUpData = centreNames.map(c => centreMap[c].followUp);

    this.barChartOptions = {
      ...this.barChartOptions,
      series: [
        {name: 'Individual', data: individualData},
        {name: 'Group', data: groupData},
        {name: 'Follow Up', data: followUpData}
      ],
      xaxis: {
        categories: centreNames
      }
    };
  }

  /**
   * ✅ Build bar chart from pre-aggregated summaryDocs.
   * summaryDocs are already filtered by centreCode (done in Firestore query),
   * so the chart accurately reflects the selected centre filter.
   */
  buildCentreChartFromSummary(summaryDocs: any[]): void {
    const centreMap: { [key: string]: { individual: number; group: number; followUp: number } } = {};

    summaryDocs.forEach((doc: any) => {
      const c = doc.centreCode || 'Unknown';
      if (!centreMap[c]) {
        centreMap[c] = {individual: 0, group: 0, followUp: 0};
      }
      centreMap[c].individual += doc.individualCount || 0;
      centreMap[c].group += doc.groupCount || 0;
      centreMap[c].followUp += doc.followUpCount || 0;
    });

    const centreCodes = Object.keys(centreMap);

    this.barChartOptions = {
      ...this.barChartOptions,
      series: [
        {name: 'Individual', data: centreCodes.map(c => centreMap[c].individual)},
        {name: 'Group', data: centreCodes.map(c => centreMap[c].group)},
        {name: 'Follow Up', data: centreCodes.map(c => centreMap[c].followUp)}
      ],
      xaxis: {categories: centreCodes}
    };
  }

  applyRoleSort() {
    this.allUsers.sort((a: Teacher, b: Teacher) => {

      const roleA = a.designation?.toLowerCase() || '';
      const roleB = b.designation?.toLowerCase() || '';

      const priorityA = this.rolePriority[roleA] || 99;
      const priorityB = this.rolePriority[roleB] || 99;

      // First sort by role hierarchy
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Then alphabetical inside same role
      return a.userName.localeCompare(b.userName);
    });

    this.dataSource.data = [...this.allUsers];
  }

  loadUsers() {
    this.loadingService.setLoading(true);
    console.log('📚 Loading teachers from employeeService... Type:', this.teachersType);

    const teachers$ = this.teachersType === 'Active'
      ? this.employeeService.getTeachers()
      : this.employeeService.getAllTeachers();

    // ✅ Use SubSink for subscription management
    this.subs.sink = teachers$.subscribe({
      next: (res: any) => {
        console.log('📦 Raw response from getTeachers:', res);
        console.log('📦 Response type:', typeof res, 'Is array:', Array.isArray(res), 'Length:', res?.length);

        if (!res || !Array.isArray(res)) {
          console.error('❌ Invalid response from getTeachers - not an array:', res);
          this.loadingService.setLoading(false);
          return;
        }

        if (res.length === 0) {
          console.error('❌ Empty response from getTeachers - no teacher records found');
          this.loadingService.setLoading(false);
          return;
        }

        console.log(`📊 Processing ${res.length} teacher records...`);

        // Track filtering reasons
        const filterStats = {
          total: res.length,
          invalidPayload: 0,
          noData: 0,
          disabled: 0,
          itStaff: 0,
          noCentres: 0,
          valid: 0
        };

        const teachers: Teacher[] = res
          .map((r: any, index: number): Teacher | null => {
            // ✅ getTeachers() returns direct objects, not snapshots
            // No need for payload.toJSON() - the data is already plain objects
            const u = r;

            if (!u) {
              filterStats.noData++;
              console.warn(`⚠️ Record ${index}: Empty record`);
              return null;
            }
            if (u.disabled === true) {
              filterStats.disabled++;
              console.log(`ℹ️ Record ${index}: Skipping disabled teacher: ${u.userName}`);
              return null;
            }
            if (u.jobtitle?.toLowerCase() === 'it') {
              filterStats.itStaff++;
              console.log(`ℹ️ Record ${index}: Skipping IT staff: ${u.userName}`);
              return null;
            }

            const role = (u.jobtitle || '').toLowerCase();

            let centres: string[] = [];
            if (u.centre) {
              if (Array.isArray(u.centre)) {
                centres = u.centre;
              } else if (typeof u.centre === 'string') {
                centres = [u.centre];
              } else {
                centres = Object.values(u.centre);
              }
            }

            if (centres.length === 0) {
              filterStats.noCentres++;
              console.warn(`⚠️ Record ${index}: Teacher ${u.userName} has no centres assigned`);
              return null;
            }

            // PAN INDIA LOGIC
            if (centres.includes('Pan India')) {
              if (role === 'lead teacher' || role === 'sr teacher') {
                centres = ['Pan India'];
              } else {
                centres = centres.filter(c => c !== 'Pan India');
              }
            }

            const teacher = {
              profileImages: u.profileImages || 'assets/img/user-avatar.png',
              userName: u.userName || 'Unknown',
              designation: u.jobtitle || 'Teacher',
              dateOfJoining: u.dateOfJoining || 'N/A',
              centre: centres,
              individualSessionCount: 0,
              groupSessionCount: 0,
              followUpCount: 0,
              sessionObserve: 0
            } as Teacher;

            filterStats.valid++;
            console.log(`✅ Record ${index}: Processed teacher: ${teacher.userName} (${teacher.designation}) - Centres: ${centres.join(', ')}`);
            return teacher;
          })
          .filter((t: Teacher | null): t is Teacher => t !== null && t.centre.length > 0);

        console.log(`📊 Filtering complete: ${teachers.length} valid teachers out of ${res.length} records`);
        console.log('📊 Filter Statistics:', {
          total: filterStats.total,
          valid: filterStats.valid,
          filtered: {
            invalidPayload: filterStats.invalidPayload,
            noData: filterStats.noData,
            disabled: filterStats.disabled,
            itStaff: filterStats.itStaff,
            noCentres: filterStats.noCentres
          }
        });

        if (teachers.length === 0) {
          console.error('❌ No valid teachers after filtering - all records were filtered out');
          console.log('📊 Why all teachers were filtered:');
          console.log(`  - Invalid payload: ${filterStats.invalidPayload} records`);
          console.log(`  - No data: ${filterStats.noData} records`);
          console.log(`  - Disabled: ${filterStats.disabled} records`);
          console.log(`  - IT Staff: ${filterStats.itStaff} records`);
          console.log(`  - No centres: ${filterStats.noCentres} records`);
          console.log('');
          console.log('💡 Possible solutions:');
          console.log('  1. Check if employeeService.getTeachers() filters by department=Education');
          console.log('  2. Verify teachers have disabled=false in database');
          console.log('  3. Verify teachers have jobtitle != "IT"');
          console.log('  4. Verify teachers have centres assigned');
          this.loadingService.setLoading(false);
          return;
        }

        this.allUsers = teachers;
        console.log(`✅ this.allUsers populated with ${this.allUsers.length} teachers`);

        // ✅ Setup teacher map for O(1) lookups
        this.setupTeacherMap();

        this.applyRoleSort();
        this.dataSource.data = [...teachers];

        console.log(`✅ DataSource updated with ${this.dataSource.data.length} teachers`);
        console.log("✅ Teachers loaded and mapped:", teachers.length);
        console.log('💡 Select a date range and click Search to load session counts.');
        this.loadingService.setLoading(false);
      },
      error: (error) => {
        console.error('❌ Error loading teachers from employeeService:', error);
        this.loadingService.setLoading(false);
      }
    });
  }

  /**
   * ✅ OPTIMIZED: Setup teacher map for O(1) lookups
   * Converts O(n²) to O(n) complexity
   */
  private setupTeacherMap(): void {
    this.teacherMap.clear();
    this.allUsers.forEach(teacher => {
      if (teacher.userName) {
        this.teacherMap.set(this.normalizeName(teacher.userName), teacher);
      }
    });
    console.log(`📊 Teacher map created with ${this.teacherMap.size} entries`);
  }

  /**
   * Loads teachers as a Promise so search() can await it
   */
  private loadUsersAsync(): Promise<Teacher[]> {
    const teachers$ = this.teachersType === 'Active'
      ? this.employeeService.getTeachers()
      : this.employeeService.getAllTeachers();

    return new Promise((resolve, reject) => {
      teachers$.pipe(
          filter((res: any) => Array.isArray(res) && res.length > 0),
          take(1)
        ).subscribe({
        next: async (res: any) => {
          if (!res || !Array.isArray(res) || res.length === 0) {
            resolve([]);
            return;
          }

          const teachersWithMissingCentres: any[] = [];
          const teachers: Teacher[] = [];

          for (const u of res) {
            if (!u) continue;
            // When "All" is selected, don't filter out disabled
            if (this.teachersType === 'Active' && u.disabled === true) continue;
            if (u.jobtitle?.toLowerCase() === 'it') continue;

            const role = (u.jobtitle || '').toLowerCase();
            let centres: string[] = [];
            if (u.centre) {
              if (Array.isArray(u.centre)) centres = u.centre;
              else if (typeof u.centre === 'string') centres = [u.centre];
              else centres = Object.values(u.centre);
            }

            // ✅ If no centres assigned, derive from individual sessions
            if (centres.length === 0) {
              teachersWithMissingCentres.push({ userData: u, role });
              continue;
            }

            if (centres.includes('Pan India')) {
              if (role === 'lead teacher' || role === 'sr teacher') {
                centres = ['Pan India'];
              } else {
                centres = centres.filter(c => c !== 'Pan India');
              }
            }

            teachers.push({
              profileImages: u.profileImages || 'assets/img/user-avatar.png',
              userName: u.userName || 'Unknown',
              designation: u.jobtitle || 'Teacher',
              centre: centres,
              disabled: u.disabled ?? false,
              individualSessionCount: 0,
              groupSessionCount: 0,
              followUpCount: 0,
              sessionObserve: 0,
              isSynthetic: false
            } as Teacher);
          }

          // ✅ OPTIMIZED: Fetch centres from sessions for teachers with missing centres
          // Fetch sessions ONCE and build a map instead of fetching for each teacher
          if (teachersWithMissingCentres.length > 0) {
            console.log(`📍 Fetching centres from sessions for ${teachersWithMissingCentres.length} teachers...`);

            try {
              // Derived from educationMonthlySummary rather than by scanning every
              // session: a summary doc is keyed {teacherName}_{yearMonth}_{centreCode},
              // so the mapping is already carried by the ~50-200 docs that back this table.
              // Fetched without a date range on purpose — the scan this replaces was
              // all-time, so a teacher whose sessions fall outside the current filter
              // must still resolve a centre.
              const centreSummaryDocs = await this.educationService
                .getSummaryForDateRange([], null, null);

              // Build a map: teacherName -> Set of centreCodes
              const teacherCentreMap = new Map<string, Set<string>>();
              centreSummaryDocs?.forEach((doc: any) => {
                const submittedBy = doc.teacherName?.toLowerCase();
                // 'All' is the fallback bucket for group sessions that carry no centre —
                // it is not a real centre, so it must not become one here.
                if (submittedBy && doc.centreCode && doc.centreCode !== 'All') {
                  if (!teacherCentreMap.has(submittedBy)) {
                    teacherCentreMap.set(submittedBy, new Set<string>());
                  }
                  teacherCentreMap.get(submittedBy)!.add(doc.centreCode);
                }
              });

              // Get all centre codes we need to convert
              const allCentreCodes = new Set<string>();
              teachersWithMissingCentres.forEach(item => {
                const key = item.userData.userName?.toLowerCase();
                const codes = teacherCentreMap.get(key);
                if (codes) {
                  codes.forEach(code => allCentreCodes.add(code));
                }
              });

              // Convert all centre codes to names in one batch
              const centreCodeArray = Array.from(allCentreCodes);
              const allCentres = await this.centres$.pipe(take(1)).toPromise();
              const codeToNameMap = new Map<string, string>();
              centreCodeArray.forEach(code => {
                const centre = allCentres?.find(c => c.centreCode === code);
                codeToNameMap.set(code, centre?.centreName || code);
              });

              // Now assign centres to teachers
              for (const item of teachersWithMissingCentres) {
                const key = item.userData.userName?.toLowerCase();
                const centreCodes = teacherCentreMap.get(key);

                if (centreCodes && centreCodes.size > 0) {
                  const centreNames = Array.from(centreCodes).map(code => codeToNameMap.get(code) || code);
                  console.log(`✅ Derived centres for ${item.userData.userName}: ${centreNames.join(', ')}`);

                  teachers.push({
                    profileImages: item.userData.profileImages || 'assets/img/user-avatar.png',
                    userName: item.userData.userName || 'Unknown',
                    designation: item.userData.jobtitle || 'Teacher',
                    centre: centreNames,
                    disabled: item.userData.disabled ?? false,
                    individualSessionCount: 0,
                    groupSessionCount: 0,
                    followUpCount: 0,
                    sessionObserve: 0,
                    isSynthetic: false
                  } as Teacher);
                }
              }
            } catch (error) {
              console.warn('⚠️ Could not fetch centres for teachers with missing centres:', error);
            }
          }

          resolve(teachers);
        },
        error: (err) => reject(err)
      });
    });
  }

  /**
   * ✅ OPTIMIZED: Convert centre codes to names in a single batch operation
   * Avoids multiple observable subscriptions
   */
  private async convertCentreCodesToNames(centreCodes: string[]): Promise<string[]> {
    if (centreCodes.length === 0) return [];

    try {
      const allCentres = await this.centres$.pipe(take(1)).toPromise();
      return centreCodes.map(code => {
        const centre = allCentres?.find(c => c.centreCode === code);
        return centre?.centreName || code; // Fallback to code if name not found
      });
    } catch (error) {
      console.warn('⚠️ Could not convert centre codes to names:', error);
      return centreCodes; // Return codes as fallback
    }
  }

  async search() {
    this.loadingService.setLoading(true);
    this.allUsers = []; // ✅ Clear previous results to prevent duplicates on re-search
    this.teacherMap.clear();

    try {
      console.log('🔍 Search initiated with filters:', {
        centres: this.selectedCentres,
        startDate: this.mStartDate,
        endDate: this.mEndDate,
        teachersType: this.teachersType
      });

      // ✅ Fetch only teachers and summary data (no individual/group sessions)
      const [teachers, summaryDocs, observations] = await Promise.all([
        this.loadUsersAsync(),
        this.educationService.getSummaryForDateRange(
          this.selectedCentres, this.mStartDate, this.mEndDate
        ),
        this.educationObservationService.getAll().pipe(take(1)).toPromise()
      ]);

      console.log(`📊 Loaded ${teachers.length} ${this.teachersType} teachers`);
      console.log(`📊 Loaded ${summaryDocs.length} summary documents`);
      this.allUsers = teachers;
      this.setupTeacherMap();


      // ====== TEACHER TABLE — from pre-aggregated summary ======
      console.log(`📊 Processing ${summaryDocs.length} summary documents...`);

      // Aggregate summary docs by teacher name (across multiple months & centres)
      const teacherSumMap = new Map<string, { individual: number; group: number; followUp: number; originalName: string }>();
      let rawIndividualSum = 0, rawGroupSum = 0, rawFollowUpSum = 0;
      const emptyNameDocs: any[] = [];
      summaryDocs.forEach((doc: any) => {
        rawIndividualSum += (doc.individualCount || 0);
        rawGroupSum += (doc.groupCount || 0);
        rawFollowUpSum += (doc.followUpCount || 0);

        const key = this.normalizeName(doc.teacherName);
        if (!key) {
          emptyNameDocs.push(doc);
          console.warn('⚠️ SummaryDoc with empty teacherName skipped:', doc);
          return;
        }
        const existing = teacherSumMap.get(key) || {individual: 0, group: 0, followUp: 0, originalName: doc.teacherName};
        teacherSumMap.set(key, {
          individual: existing.individual + (doc.individualCount || 0),
          group: existing.group + (doc.groupCount || 0),
          followUp: existing.followUp + (doc.followUpCount || 0),
          originalName: existing.originalName || doc.teacherName
        });
      });

      console.log('📋 RAW summaryDocs totals (before normalization):', {
        individual: rawIndividualSum,
        group: rawGroupSum,
        followUp: rawFollowUpSum,
        totalDocs: summaryDocs.length
      });

      console.log(`📊 Aggregated summary for ${teacherSumMap.size} unique teachers`);



      // ✅ OPTIMIZED: For synthetic teachers, use centreCode from summary instead of fetching sessions
      // This avoids fetching all individual sessions multiple times
      const excludedNames = ['master admin', 'na', 'developercode', 'admin'];

      // Track excluded-name entries for debug report
      const excludedEntries: { originalName: string; key: string; individual: number; group: number; followUp: number }[] = [];
      for (const exKey of excludedNames) {
        const entry = teacherSumMap.get(exKey);
        if (entry) {
          excludedEntries.push({ originalName: entry.originalName, key: exKey, individual: entry.individual, group: entry.group, followUp: entry.followUp });
        }
      }

      // Build a map of teacher name -> centres from summary docs
      const teacherCentresMap = new Map<string, Set<string>>();
      summaryDocs.forEach((doc: any) => {
        if (!doc.teacherName || !doc.centreCode) return;
        const key = this.normalizeName(doc.teacherName);
        if (!teacherCentresMap.has(key)) {
          teacherCentresMap.set(key, new Set<string>());
        }
        teacherCentresMap.get(key)!.add(doc.centreCode);
      });

      // Create synthetic rows for teachers in summary but not in employee list
      for (const [key, counts] of teacherSumMap) {
        if (excludedNames.includes(key)) continue;
        const exists = this.allUsers.some(
          u => this.normalizeName(u.userName) === key
        );
        if (!exists) {
          console.log(`➕ Creating synthetic teacher row for: ${counts.originalName}`);

          // Get centre codes from summary docs (much faster than fetching sessions)
          const centreCodes = teacherCentresMap.get(key) || new Set<string>();
          const centreNames = await this.convertCentreCodesToNames(Array.from(centreCodes));

          this.allUsers.push({
            profileImages: 'assets/images/userbig.png',
            userName: counts.originalName || key,
            designation: 'Teacher',
            centre: centreNames.length > 0 ? centreNames : ['—'],
            disabled: false,
            individualSessionCount: 0,
            groupSessionCount: 0,
            followUpCount: 0,
            sessionObserve: 0,
            isSynthetic: true
          } as Teacher);
        }
      }

      // Remove excluded names from the list
      this.allUsers = this.allUsers.filter(
        u => !excludedNames.includes(this.normalizeName(u.userName))
      );

      // ✅ Sort so real employees come before synthetics (ensures real entry wins deduplication)
      this.allUsers.sort((a, b) => (a.isSynthetic ? 1 : 0) - (b.isSynthetic ? 1 : 0));

      // ✅ Deduplicate by normalized name (keep first occurrence — real employee over synthetic)
      const seenNames = new Set<string>();
      this.allUsers = this.allUsers.filter(u => {
        const key = this.normalizeName(u.userName);
        if (seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      });

      // ✅ Step 3: Reset counts & assign from aggregated data
      this.allUsers.forEach(teacher => {
        const key = this.normalizeName(teacher.userName);
        const counts = teacherSumMap.get(key);
        if (counts) {
          teacher.individualSessionCount = counts.individual;
          teacher.groupSessionCount = counts.group;
          teacher.followUpCount = counts.followUp;
        } else {
          teacher.individualSessionCount = 0;
          teacher.groupSessionCount = 0;
          teacher.followUpCount = 0;
        }
      });

      // ✅ Step 4: When specific centres are selected, only show teachers who have
      // sessions in those centres (i.e. their name appears in the filtered summaryDocs).
      let filteredForTable = this.selectedCentres.length > 0
        ? this.allUsers.filter(t => teacherSumMap.has(this.normalizeName(t.userName)))
        : this.allUsers;

      // Hide inactive and synthetic (unknown/deleted) teachers when Active filter is applied
      if (this.teachersType === 'Active') {
        filteredForTable = filteredForTable.filter(t => !t.disabled && !t.isSynthetic);
      }

      // Apply role sort
      filteredForTable.sort((a, b) => {
        const priorityA = this.rolePriority[a.designation?.toLowerCase() || ''] || 99;
        const priorityB = this.rolePriority[b.designation?.toLowerCase() || ''] || 99;
        if (priorityA !== priorityB) return priorityA - priorityB;
        return a.userName.localeCompare(b.userName);
      });

      // Update table
      this.dataSource.data = [...filteredForTable];

      // ─── Count observations per observer name (Dwani & Ankita) ───
      const observationMap = new Map<string, number>();
      (observations as any[] || []).forEach((obs: any) => {
        const key = this.normalizeName(obs.observerName);
        if (!key) return;
        observationMap.set(key, (observationMap.get(key) || 0) + 1);
      });
      this.dataSource.data.forEach((teacher: Teacher) => {
        teacher.sessionObserve = observationMap.get(this.normalizeName(teacher.userName)) || 0;
      });
      this.tableObservedTotal = this.dataSource.data.reduce(
        (sum: number, t: Teacher) => sum + (t.sessionObserve || 0), 0
      );

      // ====== CALCULATE STAT CARDS FROM TABLE CONTENT ======
      // This recalculates the stats to exactly mirror what the user is currently seeing after filters.
      const summaryIndividualTotal = this.totalIndividual;
      const summaryGroupTotal = this.totalGroup;
      const summaryFollowUpTotal = this.totalFollowUp;

      this.educationSessionCounts = {
        totalSessions: summaryIndividualTotal,
        teacherCount: summaryIndividualTotal,
        volunteerCount: 0
      };

      this.groupSessionCounts = {
        totalSessions: summaryGroupTotal,
        teacherCount: summaryGroupTotal,
        volunteerCount: 0
      };

      this.followUpCounts = summaryFollowUpTotal;
      this.updateCombinedCounts();

      // ─── Build session-attribution debug report ──────────────────────────────
      const missingLogs: MissingSessionsLogEdu[] = [];

      // Group A — empty-name docs grouped by centre
      if (emptyNameDocs.length > 0) {
        const byCentre: { [c: string]: { ind: number; grp: number; fu: number; count: number } } = {};
        emptyNameDocs.forEach((d: any) => {
          const c = d.centreCode || 'Unknown';
          if (!byCentre[c]) byCentre[c] = { ind: 0, grp: 0, fu: 0, count: 0 };
          byCentre[c].ind   += (d.individualCount || 0);
          byCentre[c].grp   += (d.groupCount      || 0);
          byCentre[c].fu    += (d.followUpCount   || 0);
          byCentre[c].count += 1;
        });
        Object.entries(byCentre).forEach(([centreCode, v]) => {
          missingLogs.push({
            reason: 'Empty Name (Excluded)',
            teacherName: '(empty / null)',
            normalizedKey: '',
            individual: v.ind,
            group: v.grp,
            followUp: v.fu,
            centreCode,
            docCount: v.count,
          });
        });
      }

      // Group B — excluded-name entries
      excludedEntries.forEach(m => {
        missingLogs.push({
          reason: 'Excluded Name (Filtered)',
          teacherName: m.originalName,
          normalizedKey: m.key,
          individual: m.individual,
          group: m.group,
          followUp: m.followUp,
        });
      });

      // Group C — Active mode: summary entries with no matching teacher row
      if (this.teachersType === 'Active') {
        teacherSumMap.forEach((counts, key) => {
          if (excludedNames.includes(key)) return;
          const hasRow = this.dataSource.data.some(t => this.normalizeName(t.userName) === key);
          if (!hasRow) {
            missingLogs.push({
              reason: 'Unmatched (Active Mode)',
              teacherName: counts.originalName,
              normalizedKey: key,
              individual: counts.individual,
              group: counts.group,
              followUp: counts.followUp,
            });
          }
        });
      }

      this.debugReport = {
        rawTotals: {
          individual: rawIndividualSum,
          group:      rawGroupSum,
          followUp:   rawFollowUpSum,
          total:      rawIndividualSum + rawGroupSum + rawFollowUpSum,
        },
        cardTotals: {
          individual: this.totalIndividual,
          group:      this.totalGroup,
          followUp:   this.totalFollowUp,
          total:      this.totalIndividual + this.totalGroup + this.totalFollowUp,
        },
        diff: {
          individual: rawIndividualSum - this.totalIndividual,
          group:      rawGroupSum      - this.totalGroup,
          followUp:   rawFollowUpSum   - this.totalFollowUp,
          total:      (rawIndividualSum + rawGroupSum + rawFollowUpSum) -
                      (this.totalIndividual + this.totalGroup + this.totalFollowUp),
        },
        logs: missingLogs,
        generatedAt: new Date(),
      };
      // ─────────────────────────────────────────────────────────────────────────

      console.log(`📊 Table shows ${filteredForTable.length} teachers (${filteredForTable.filter(t => t.isSynthetic).length} synthetic)`);

      // ✅ Build chart from summaryDocs
      this.buildCentreChartFromSummary(summaryDocs);

      // Clear boys vs girls chart data since we don't have session-level data
      this.educationSessions = [];
      this.groupSessions = [];
      this.followUpSessions = [];

      this.loadingService.setLoading(false);
      this.cdr.detectChanges(); // ✅ Force Angular to pick up changes from async operations
      console.log('✅ Search completed:', {
        teachersType: this.teachersType,
        totalTeachers: this.allUsers.length,
        syntheticTeachers: this.allUsers.filter(t => t.isSynthetic).length,
        summaryDocs: summaryDocs.length,
        individualTotal: summaryIndividualTotal,
        groupTotal: summaryGroupTotal,
        followUpTotal: summaryFollowUpTotal
      });

    } catch (error) {
      console.error('❌ Error in search:', error);
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  async loadSessionCountsForCards(centreCode: string, startDate: Date | null | undefined, endDate: Date | null | undefined) {
    const snapshot = await this.educationService.individualDashboardSearchData(
      centreCode, startDate, endDate
    );

    this.educationSessions = snapshot.docs.map((doc: any) => ({id: doc.id, ...doc.data()}));

    let totalSessions = 0, teacherCount = 0, volunteerCount = 0;

    this.educationSessions.forEach((data: any) => {
      if (data.present === 'Present') {
        totalSessions++;
        if (data.conductedBy === "By Teacher") teacherCount++;
        else if (data.conductedBy === "By Volunteer") volunteerCount++;
      }
    });

    this.educationSessionCounts = {totalSessions, teacherCount, volunteerCount};
    this.updateCombinedCounts();
  }

  async loadGroupSessionCountsForCards(centreCode: string, startDate: Date | null | undefined, endDate: Date | null | undefined) {
    const snapshot = await this.educationService.getGroupEducationSessionsCount(
      centreCode, startDate, endDate
    );

    this.groupSessions = snapshot.docs.map((doc: any) => ({id: doc.id, ...doc.data()}));

    let totalSessions = 0, teacherCount = 0, volunteerCount = 0;

    this.groupSessions.forEach((data: any) => {
      const hasPresentChild = data.children?.some((child: any) => child.status === "Present");
      if (hasPresentChild) {
        totalSessions++;
        if (data.conductedBy === "By Teacher") teacherCount++;
        else if (data.conductedBy === "By Volunteer") volunteerCount++;
      }
    });

    this.groupSessionCounts = {totalSessions, teacherCount, volunteerCount};
    this.updateCombinedCounts();
  }

  private updateCombinedCounts() {
    this.combinedSessionCounts = {
      totalSessions:
        (this.educationSessionCounts?.totalSessions || 0) +
        (this.groupSessionCounts?.totalSessions || 0),
      teacherCount:
        (this.educationSessionCounts?.teacherCount || 0) +
        (this.groupSessionCounts?.teacherCount || 0),
      volunteerCount:
        (this.educationSessionCounts?.volunteerCount || 0) +
        (this.groupSessionCounts?.volunteerCount || 0),
    };
  }

  loadBoysVsGirlsChart() {
    let boys = 0;
    let girls = 0;

    this.groupSessions.forEach((session: any) => {
      session.children?.forEach((child: any) => {
        if (child.status === 'Present') {
          if (child.gender === 'Male') boys++;
          if (child.gender === 'Female') girls++;
        }
      });
    });

    this.educationSessions.forEach((session: any) => {
      if (session.present === 'Present') {
        if (session.gender === 'Male') boys++;
        if (session.gender === 'Female') girls++;
      }
    });

    this.followUpSessions.forEach((session: any) => {
      if (session.gender === 'Male') boys++;
      if (session.gender === 'Female') girls++;
    });

    this.chartOptionsArea = {
      series: [
        {name: "Boys", data: [boys]},
        {name: "Girls", data: [girls]}
      ],
      chart: {type: "area", height: 350, toolbar: {show: false}},
      dataLabels: {enabled: false},
      stroke: {curve: "smooth", width: 3},
      xaxis: {categories: ["Total Sessions"]},
      yaxis: {title: {text: "Boys Vs Girls"}},
      colors: ["#fc8380", "#6973c6"],
      fill: {type: "gradient"},
      legend: {position: "top", horizontalAlign: "left"}
    };
  }

  get totalIndividual(): number {
    return this.dataSource?.data?.reduce((sum: number, t: Teacher) => sum + (t.individualSessionCount || 0), 0) || 0;
  }

  get totalGroup(): number {
    return this.dataSource?.data?.reduce((sum: number, t: Teacher) => sum + (t.groupSessionCount || 0), 0) || 0;
  }

  get totalFollowUp(): number {
    return this.dataSource?.data?.reduce((sum: number, t: Teacher) => sum + (t.followUpCount || 0), 0) || 0;
  }

  get totalAttributedIndividual(): number {
    return this.debugReport?.logs.reduce((s, l) => s + l.individual, 0) ?? 0;
  }
  get totalAttributedGroup(): number {
    return this.debugReport?.logs.reduce((s, l) => s + l.group, 0) ?? 0;
  }
  get totalAttributedFollowUp(): number {
    return this.debugReport?.logs.reduce((s, l) => s + l.followUp, 0) ?? 0;
  }

  getLogRowClass(reason: string): string {
    if (reason === 'Excluded Name (Filtered)') return 'table-info';
    if (reason === 'Unmatched (Active Mode)') return 'table-warning';
    return '';
  }

  getLogBadgeClass(reason: string): string {
    if (reason === 'Excluded Name (Filtered)') return 'bg-info text-dark';
    if (reason === 'Unmatched (Active Mode)') return 'bg-warning text-dark';
    return 'bg-secondary';
  }

  get dateRangeLabel(): string {
    if (!this.mStartDate && !this.mEndDate) return 'All Time';
    const fmt = (d: Date) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    if (this.mStartDate && this.mEndDate) return `${fmt(this.mStartDate)} – ${fmt(this.mEndDate)}`;
    if (this.mStartDate) return `From ${fmt(this.mStartDate)}`;
    return `Up to ${fmt(this.mEndDate!)}`;
  }

  getPhoto(img?: string): string {
    return img && img.trim() !== '' ? img : 'assets/images/userbig.png';
  }

  onImageError(event: any) {
    event.target.src = 'assets/images/userbig.png';
  }

  /**
   * Get display name with "(Inactive)" suffix for synthetic or disabled users
   */
  getDisplayName(teacher: Teacher): string {
    const isInactive = teacher.isSynthetic || teacher.disabled;
    return isInactive ? `${teacher.userName} (Inactive)` : teacher.userName;
  }

  toggleCentre(row: Teacher) {
    row.expanded = !row.expanded;
  }

  resetDate() {
    this.mStartDate = null; // Reset to fetch all data from the beginning
    this.mEndDate = new Date();
    this.selectedCentres = [];
    this.selectedCentreObjects = [];
    this.educationSessionCounts = null;
    this.groupSessionCounts = null;
    this.followUpCounts = 0;
    this.combinedSessionCounts = null;
  }

  onChildCentreSelect(selected: any) {
    if (Array.isArray(selected)) {
      this.childSelectedCentreObjects = selected;
      this.childSelectedCentres = selected.map((c: any) => typeof c === 'object' ? c.centreCode : c).filter(Boolean);
    } else if (selected && typeof selected === 'object') {
      this.childSelectedCentreObjects = [selected];
      this.childSelectedCentres = [selected.centreCode];
    } else {
      this.childSelectedCentreObjects = [];
      this.childSelectedCentres = [];
    }
  }

  async searchChildrenTable() {
    if (!this.childStartDate || !this.childEndDate) {
      alert('Please select a date range before searching.');
      return;
    }
    this.loadingService.setLoading(true);
    try {
      const summaryCodes = this.childSelectedCentres.length > 0 ? this.childSelectedCentres : [];
      const summaryDocs = await this.educationService.getSummaryForDateRange(
        summaryCodes, this.childStartDate, this.childEndDate
      );
      await this.loadCentreChildrenTable(summaryDocs, this.childSelectedCentres, this.childStartDate, this.childEndDate);
    } catch (e) {
      console.error('Error loading children table:', e);
    }
    this.loadingService.setLoading(false);
    this.cdr.detectChanges();
  }

  clearChildFilters() {
    this.childStartDate = null;
    this.childEndDate = null;
    this.childSelectedCentres = [];
    this.childSelectedCentreObjects = [];
    this.centreChildrenDataSource.data = [];
  }

  private async loadCentreChildrenTable(
    summaryDocs: any[],
    overrideCentres?: string[],
    overrideStart?: Date | null,
    overrideEnd?: Date | null
  ): Promise<void> {
    const allCentres = await this.centres$.pipe(take(1)).toPromise();
    const activeCentres = overrideCentres ?? this.selectedCentres;
    const activeStart = overrideStart !== undefined ? overrideStart : this.mStartDate;
    const activeEnd = overrideEnd !== undefined ? overrideEnd : this.mEndDate;

    const centreCodesToQuery = activeCentres.length > 0
      ? activeCentres
      : Array.from(new Set(summaryDocs.map((d: any) => d.centreCode).filter(Boolean)));

    if (centreCodesToQuery.length === 0) {
      this.centreChildrenDataSource.data = [];
      return;
    }

    const followUpByCentre = new Map<string, number>();
    summaryDocs.forEach((d: any) => {
      if (!d.centreCode) return;
      followUpByCentre.set(d.centreCode, (followUpByCentre.get(d.centreCode) || 0) + (d.followUpCount || 0));
    });

    const date = activeStart ? this.formatDate(activeStart) : (activeEnd ? this.formatDate(activeEnd) : 'All Time');

    const rows: CentreChildrenRow[] = [];

    await Promise.all(centreCodesToQuery.map(async (code: string, idx: number) => {
      try {
        const children = await this.childService.getAllotedChildListByCenter(code).toPromise() as any[];
        const centreObj = allCentres?.find(c => c.centreCode === code);
        const centreName = centreObj?.centreName || code;

        const followUp = followUpByCentre.get(code) || 0;
        const below14 = children?.filter((c: any) => c.age >= 4 && c.age <= 14).length || 0;
        const above14 = children?.filter((c: any) => c.age > 14).length || 0;
        const above14Foll = 0;
        const total = below14 + followUp + above14 + above14Foll;

        rows.push({
          srNo: idx + 1,
          date,
          centreName,
          centreCode: code,
          below14New: below14,
          below14Foll: followUp,
          above14New: above14,
          above14Foll,
          total
        });
      } catch (e) {
        console.warn(`⚠️ Could not load children for centre ${code}:`, e);
      }
    }));

    rows.sort((a, b) => a.centreName.localeCompare(b.centreName));
    rows.forEach((r, i) => r.srNo = i + 1);
    this.centreChildrenDataSource.data = rows;
  }

  private formatDate(d: Date): string {
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  get totalBelow14New(): number { return this.centreChildrenDataSource.data.reduce((s, r) => s + r.below14New, 0); }
  get totalBelow14Foll(): number { return this.centreChildrenDataSource.data.reduce((s, r) => s + r.below14Foll, 0); }
  get totalAbove14New(): number { return this.centreChildrenDataSource.data.reduce((s, r) => s + r.above14New, 0); }
  get totalAbove14Foll(): number { return this.centreChildrenDataSource.data.reduce((s, r) => s + r.above14Foll, 0); }
  get totalCentreChildren(): number { return this.centreChildrenDataSource.data.reduce((s, r) => s + r.below14New + r.below14Foll + r.above14New + r.above14Foll, 0); }

  ngOnDestroy(): void {
    // ✅ Clean up all subscriptions with SubSink
    this.subs.unsubscribe();
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }

  /**
   * ✅ TrackBy function for better ngFor performance in templates
   */
  trackByUserName(index: number, teacher: Teacher): string {
    return teacher.userName;
  }

  private getCurrentUserNotifications(email: string) {

    this.subs.sink = this.mNotificationService.getUserNotifications(email).subscribe(res => {
      this.notifications = res as NotificationModel[]
      console.log(`this user ${this.userData.userName} has ${this.notifications.length} notifications`)
    })


  }

  protected onTeacherTypeChange($event: MatSelectChange) {
    this.teachersType = $event.value;
    this.search(); // Fetch the new data when the filter changes
  }

  protected onSelectedCentres(selected: any) {
    if (Array.isArray(selected)) {
      this.selectedCentreObjects = selected;
      this.selectedCentres = selected.map((c: any) => typeof c === 'object' ? c.centreCode : c).filter(Boolean);
    } else if (selected && typeof selected === 'object') {
      this.selectedCentreObjects = [selected];
      this.selectedCentres = [selected.centreCode];
    } else {
      this.selectedCentreObjects = [];
      this.selectedCentres = [];
    }
    console.log('Selected centre codes:', this.selectedCentres);
  }

  private populateSelectedCentreObjectsFromCodes(codes: string[]) {
    if (!codes || codes.length === 0) {
      this.selectedCentreObjects = [];
      return;
    }
    this.centres$.pipe(take(1)).subscribe(arr => {
      this.selectedCentreObjects = codes.map(code => {
        if (code === 'All Centres') return { centreCode: 'All Centres', centreName: 'All Centres' };
        return arr?.find(c => c.centreCode === code) || { centreCode: code, centreName: code };
      });
    });
  }
}
